import React, { useState, useCallback } from 'react';
import { collection, addDoc, Timestamp, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { Lead, LeadSource, LeadStatus, EnhancedDeal, DealOutcome, Pipeline } from '../types';
import { formatLocationForMaps } from '../utils/locationFormatter';
import { geocodeAddress } from '../utils/geocoding';
import { getSourceColor } from '../utils/sourceColors';
import {
  Package,
  User,
  DollarSign,
  RefreshCw,
  Link as LinkIcon,
  MapPin,
  Calendar,
  AlertTriangle,
  Check,
  Folder,
  Upload,
  Rocket,
  Lightbulb,
} from 'lucide-react';
import './ImportPipedrive.css';

type ImportType = 'people' | 'deals' | 'leads';

interface ImportedLead {
  id: string;
  pipedrivePersonId?: string;
  firstName: string;
  lastName: string;
  email: string;
}

// Pipedrive column mappings for PEOPLE
const PEOPLE_COLUMN_MAPPINGS: Record<string, string[]> = {
  firstName: ['Person - First name', 'First name', 'First Name', 'firstName'],
  lastName: ['Person - Last name', 'Last name', 'Last Name', 'lastName'],
  email: ['Person - Email', 'Email', 'email', 'Person - Primary email'],
  phone: ['Person - Phone', 'Phone', 'phone', 'Person - Primary phone'],
  company: ['Person - Organization', 'Organization', 'Company', 'company', 'Person - Organization name'],
  location: ['Person - Address', 'Address', 'Location', 'location', 'City', 'Person - City'],
  pipedrivePersonId: ['Person - ID', 'ID', 'Person ID'],
  pipedriveLabel: ['Person - Label', 'Label'],
  pipedriveOwner: ['Person - Owner', 'Owner', 'Person - Owner name'],
  notes: ['Person - Notes', 'Notes', 'Description'],
  pipedriveOpenDeals: ['Person - Open deals', 'Open deals'],
  pipedriveWonDeals: ['Person - Won deals', 'Won deals'],
  pipedriveLostDeals: ['Person - Lost deals', 'Lost deals'],
  pipedriveClosedDeals: ['Person - Closed deals', 'Closed deals'],
  createdAt: ['Person - Person created', 'Person created', 'Created'],
  updatedAt: ['Person - Update time', 'Update time', 'Updated'],
  source: ['Person - Source', 'Source', 'Lead source'],
};

// Pipedrive column mappings for DEALS
const DEALS_COLUMN_MAPPINGS: Record<string, string[]> = {
  title: ['Deal - Title', 'Title', 'Deal title', 'Deal - Deal title'],
  value: ['Deal - Value', 'Value', 'Deal value', 'Deal - Deal value'],
  currency: ['Deal - Currency', 'Currency'],
  personId: ['Deal - Person ID', 'Person - ID', 'Person ID', 'Deal - Contact person ID'],
  personName: ['Deal - Person', 'Person', 'Contact person', 'Deal - Contact person'],
  organizationName: ['Deal - Organization', 'Organization', 'Deal - Organization name'],
  stage: ['Deal - Stage', 'Stage', 'Deal stage'],
  pipeline: ['Deal - Pipeline', 'Pipeline'],
  status: ['Deal - Status', 'Status', 'Deal status'],
  owner: ['Deal - Owner', 'Owner', 'Deal - Owner name'],
  createdAt: ['Deal - Deal created', 'Deal created', 'Created'],
  updatedAt: ['Deal - Update time', 'Update time', 'Updated'],
  closedAt: ['Deal - Won time', 'Won time', 'Deal - Lost time', 'Lost time', 'Close time'],
  expectedCloseDate: ['Deal - Expected close date', 'Expected close date', 'Close date'],
  lostReason: ['Deal - Lost reason', 'Lost reason'],
  probability: ['Deal - Probability', 'Probability'],
  pipedriveDealId: ['Deal - ID', 'ID', 'Deal ID'],
};

// Pipedrive column mappings for LEADS (Leads Inbox)
const LEADS_COLUMN_MAPPINGS: Record<string, string[]> = {
  // Link to People
  personId: ['Lead - Person ID', 'Person ID', 'Person - ID'],

  // Core fields
  title: ['Lead - Title', 'Title', 'Lead title'],
  firstName: ['First Name', 'FirstName', 'First name', 'firstName'],
  lastName: ['Last Name', 'LastName', 'Last name', 'lastName'],
  personName: ['Lead - Person', 'Person', 'Contact'],
  organizationName: ['Lead - Organization', 'Organization', 'Company', 'company'],
  email: ['Lead - Email', 'Email', 'email'],
  phone: ['Lead - Phone', 'Phone', 'phone', 'Phone Number'],

  // Project details
  projectAddress: ['Lead - Project Address', 'Project Address', 'Address', 'Location', 'location'],
  value: ['Lead - Value', 'Value', 'Budget', 'budget'],

  // Source & Labels
  source: ['Lead - Source channel', 'Lead - Source', 'Source channel', 'Source', 'source'],
  labels: ['Lead - Label', 'Label', 'Labels', 'labels', 'Lead - Labels'],

  // MetricsLite
  metricsLiteId: ['Lead - Metrics Lite ID', 'Metrics Lite ID', 'MetricsLite ID', 'metricsLiteId', 'Visitor ID'],

  // Owner & Dates
  owner: ['Lead - Owner', 'Owner'],
  createdAt: ['Lead - Lead created', 'Lead created', 'Created', 'createdAt'],
  updatedAt: ['Lead - Update time', 'Update time', 'Updated', 'updatedAt'],

  // Pipedrive IDs
  pipedriveLeadId: ['Lead - ID', 'ID', 'Lead ID'],
};

// Source mapping
const SOURCE_MAPPING: Record<string, LeadSource> = {
  'facebook': 'facebook-form',
  'facebook form': 'facebook-form',
  'facebook-form': 'facebook-form',
  'instagram': 'instagram-form',
  'instagram form': 'instagram-form',
  'instagram-form': 'instagram-form',
  'web': 'web-form',
  'web form': 'web-form',
  'website': 'web-form',
  'ebook': 'ebook',
  'referral': 'referral',
  'cold call': 'cold-call',
  'cold-call': 'cold-call',
  'call': 'call-in',
  'call-in': 'call-in',
  'b2b': 'b2b-visit',
  'b2b-visit': 'b2b-visit',
};

const ImportPipedrive: React.FC = () => {
  const { user } = useAuth();
  const { pipelines, initializeDefaultPipelines } = useCRM();

  // State for each import type
  const [activeImport, setActiveImport] = useState<ImportType>('people');
  const [files, setFiles] = useState<Record<ImportType, File | null>>({ people: null, deals: null, leads: null });
  const [headers, setHeaders] = useState<Record<ImportType, string[]>>({ people: [], deals: [], leads: [] });
  const [parsedData, setParsedData] = useState<Record<ImportType, any[]>>({ people: [], deals: [], leads: [] });
  const [columnMappings, setColumnMappings] = useState<Record<ImportType, Record<string, string>>>({
    people: {}, deals: {}, leads: {}
  });

  // Import state
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<{ type: 'info' | 'success' | 'error'; message: string } | null>(null);
  const [shouldGeocode, setShouldGeocode] = useState(true);

  // Track imported leads for linking deals
  const [importedLeads, setImportedLeads] = useState<ImportedLead[]>([]);

  // Pipeline selection for deals
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');

  // Initialize pipelines
  React.useEffect(() => {
    if (pipelines.length === 0) {
      initializeDefaultPipelines();
    } else if (!selectedPipelineId) {
      const defaultPipeline = pipelines.find(p => p.isDefault) || pipelines[0];
      if (defaultPipeline?.id) {
        setSelectedPipelineId(defaultPipeline.id);
      }
    }
  }, [pipelines, initializeDefaultPipelines, selectedPipelineId]);

  // Parse CSV
  const parseCSV = (text: string): { headers: string[]; rows: any[] } => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    const csvHeaders = parseCSVLine(lines[0]);
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === csvHeaders.length) {
        const row: Record<string, string> = {};
        csvHeaders.forEach((header, idx) => {
          row[header] = values[idx];
        });
        rows.push(row);
      }
    }

    return { headers: csvHeaders, rows };
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"' && inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Get mapping definitions for current import type
  const getMappingDefs = (type: ImportType) => {
    switch (type) {
      case 'people': return PEOPLE_COLUMN_MAPPINGS;
      case 'deals': return DEALS_COLUMN_MAPPINGS;
      case 'leads': return LEADS_COLUMN_MAPPINGS;
    }
  };

  // Auto-detect column mappings
  const autoMapColumns = useCallback((csvHeaders: string[], type: ImportType) => {
    const mappings: Record<string, string> = {};
    const defs = getMappingDefs(type);

    for (const [crmField, possibleColumns] of Object.entries(defs)) {
      for (const col of possibleColumns) {
        const match = csvHeaders.find(h =>
          h.toLowerCase().trim() === col.toLowerCase().trim()
        );
        if (match) {
          mappings[crmField] = match;
          break;
        }
      }
    }

    return mappings;
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>, type: ImportType) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    setFiles(prev => ({ ...prev, [type]: uploadedFile }));
    setStatus({ type: 'info', message: 'Parsing file...' });

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const { headers: csvHeaders, rows } = parseCSV(text);

      setHeaders(prev => ({ ...prev, [type]: csvHeaders }));
      setParsedData(prev => ({ ...prev, [type]: rows }));

      const mappings = autoMapColumns(csvHeaders, type);
      setColumnMappings(prev => ({ ...prev, [type]: mappings }));

      const mappedCount = Object.keys(mappings).length;
      setStatus({
        type: 'success',
        message: `Parsed ${rows.length} ${type}. Auto-mapped ${mappedCount} columns.`
      });
    };
    reader.readAsText(uploadedFile);
  }, [autoMapColumns]);

  // Get mapped value
  const getMappedValue = (row: any, field: string, type: ImportType): string => {
    const csvColumn = columnMappings[type][field];
    if (!csvColumn) return '';
    return (row[csvColumn] || '').trim();
  };

  // Parse date
  const parseDate = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined;
    let date = new Date(dateStr);
    if (!isNaN(date.getTime())) return date;

    const match = dateStr.match(/(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/);
    if (match) {
      date = new Date(`${match[1]}T${match[2]}Z`);
      if (!isNaN(date.getTime())) return date;
    }
    return undefined;
  };

  // Map source
  const mapSource = (sourceStr: string): LeadSource => {
    const normalized = sourceStr.toLowerCase().trim();
    return SOURCE_MAPPING[normalized] || 'unspecified';
  };

  // Convert People row to Lead
  const personToLead = (row: any): Partial<Lead> => {
    return {
      firstName: getMappedValue(row, 'firstName', 'people') || 'Unknown',
      lastName: getMappedValue(row, 'lastName', 'people') || '',
      email: getMappedValue(row, 'email', 'people') || 'unknown@import.local',
      phone: getMappedValue(row, 'phone', 'people') || undefined,
      company: getMappedValue(row, 'company', 'people') || undefined,
      location: formatLocationForMaps(getMappedValue(row, 'location', 'people')) || '',
      notes: getMappedValue(row, 'notes', 'people') || undefined,
      source: mapSource(getMappedValue(row, 'source', 'people')),
      status: 'new' as LeadStatus,
      projectType: 'Unspecified',
      budget: 0,
      timeline: 'TBD',
      archived: false,
      geocoded: false,
      labels: [],
      pipedrivePersonId: getMappedValue(row, 'pipedrivePersonId', 'people') || undefined,
      pipedriveLabel: getMappedValue(row, 'pipedriveLabel', 'people') || undefined,
      pipedriveOwner: getMappedValue(row, 'pipedriveOwner', 'people') || undefined,
      pipedriveOpenDeals: parseInt(getMappedValue(row, 'pipedriveOpenDeals', 'people')) || undefined,
      pipedriveWonDeals: parseInt(getMappedValue(row, 'pipedriveWonDeals', 'people')) || undefined,
      pipedriveLostDeals: parseInt(getMappedValue(row, 'pipedriveLostDeals', 'people')) || undefined,
      pipedriveClosedDeals: parseInt(getMappedValue(row, 'pipedriveClosedDeals', 'people')) || undefined,
      createdAt: parseDate(getMappedValue(row, 'createdAt', 'people')),
      updatedAt: parseDate(getMappedValue(row, 'updatedAt', 'people')),
    };
  };

  // Convert Pipedrive Lead row to Lead, optionally linking to existing Person
  const pipedriveLeadToLead = (row: any, existingLeads: ImportedLead[]): Partial<Lead> & { linkedPersonId?: string } => {
    const title = getMappedValue(row, 'title', 'leads');
    const personName = getMappedValue(row, 'personName', 'leads');
    const personId = getMappedValue(row, 'personId', 'leads');

    // Check if this lead links to an existing Person (imported from People CSV)
    let linkedPerson: ImportedLead | undefined;
    if (personId) {
      linkedPerson = existingLeads.find(l => l.pipedrivePersonId === personId);
    }

    // Get firstName/lastName - prefer direct columns, then linked person, then split personName
    let firstName = getMappedValue(row, 'firstName', 'leads');
    let lastName = getMappedValue(row, 'lastName', 'leads');

    if (!firstName && linkedPerson) {
      firstName = linkedPerson.firstName;
      lastName = linkedPerson.lastName;
    } else if (!firstName && personName) {
      const parts = personName.split(' ');
      firstName = parts[0] || 'Unknown';
      lastName = parts.slice(1).join(' ');
    } else if (!firstName && title) {
      firstName = title;
    }

    firstName = firstName || 'Unknown';
    lastName = lastName || '';

    // Get email - prefer direct column, then linked person
    let email = getMappedValue(row, 'email', 'leads');
    if (!email && linkedPerson) {
      email = linkedPerson.email;
    }
    email = email || 'unknown@import.local';

    // Parse labels (comma or semicolon separated)
    const labelsStr = getMappedValue(row, 'labels', 'leads');
    const labels: string[] = labelsStr
      ? labelsStr.split(/[,;]/).map(l => l.trim()).filter(l => l)
      : [];

    // Project address
    const projectAddress = getMappedValue(row, 'projectAddress', 'leads');

    return {
      firstName,
      lastName,
      email,
      phone: getMappedValue(row, 'phone', 'leads') || undefined,
      company: getMappedValue(row, 'organizationName', 'leads') || undefined,
      location: formatLocationForMaps(projectAddress) || '',
      source: mapSource(getMappedValue(row, 'source', 'leads')),
      status: 'new' as LeadStatus,
      projectType: 'Unspecified',
      budget: parseFloat(getMappedValue(row, 'value', 'leads')) || 0,
      timeline: 'TBD',
      archived: false,
      geocoded: false,
      labels,
      // MetricsLite ID
      metricsLiteId: getMappedValue(row, 'metricsLiteId', 'leads') || undefined,
      // Pipedrive fields
      pipedrivePersonId: personId || undefined,
      pipedriveOwner: getMappedValue(row, 'owner', 'leads') || undefined,
      createdAt: parseDate(getMappedValue(row, 'createdAt', 'leads')),
      updatedAt: parseDate(getMappedValue(row, 'updatedAt', 'leads')),
      // Mark if linked to existing person (for potential merge/skip logic)
      linkedPersonId: linkedPerson?.id,
    };
  };

  // Convert Deal row to EnhancedDeal
  const dealToEnhancedDeal = async (row: any, existingLeads: ImportedLead[]): Promise<Partial<EnhancedDeal> | null> => {
    const personId = getMappedValue(row, 'personId', 'deals');
    const personName = getMappedValue(row, 'personName', 'deals');
    const statusStr = getMappedValue(row, 'status', 'deals').toLowerCase();

    // Find linked lead
    let linkedLead: ImportedLead | undefined;
    if (personId) {
      linkedLead = existingLeads.find(l => l.pipedrivePersonId === personId);
    }
    if (!linkedLead && personName) {
      // Try matching by name
      const nameParts = personName.toLowerCase().split(' ');
      linkedLead = existingLeads.find(l =>
        l.firstName.toLowerCase() === nameParts[0] &&
        (nameParts.length < 2 || l.lastName.toLowerCase().includes(nameParts[1]))
      );
    }

    // Determine outcome
    let outcome: DealOutcome = 'active';
    if (statusStr === 'won') outcome = 'won';
    else if (statusStr === 'lost') outcome = 'lost';

    const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId);
    if (!selectedPipeline) return null;

    const firstStage = selectedPipeline.stages.sort((a, b) => a.order - b.order)[0];

    return {
      pipelineId: selectedPipelineId,
      stageId: firstStage?.id || 'qualified',
      leadId: linkedLead?.id,
      contactId: linkedLead?.id || '',
      contactName: personName || 'Unknown',
      company: getMappedValue(row, 'organizationName', 'deals') || undefined,
      title: getMappedValue(row, 'title', 'deals') || 'Imported Deal',
      value: parseFloat(getMappedValue(row, 'value', 'deals')) || 0,
      outcome,
      probability: parseInt(getMappedValue(row, 'probability', 'deals')) || firstStage?.probability || 20,
      stageHistory: [{
        fromStage: null,
        toStage: firstStage?.id || 'qualified',
        transitionedAt: new Date(),
        durationMs: 0,
        transitionedBy: 'pipedrive-import',
      }],
      currentStageEnteredAt: new Date(),
      colorBar: getSourceColor('unspecified'),
      projectType: 'Imported from Pipedrive',
      location: '',
      budget: parseFloat(getMappedValue(row, 'value', 'deals')) || 0,
      source: 'unspecified' as LeadSource,
      assignedTo: getMappedValue(row, 'owner', 'deals') || undefined,
      createdAt: parseDate(getMappedValue(row, 'createdAt', 'deals')),
      updatedAt: parseDate(getMappedValue(row, 'updatedAt', 'deals')),
      closedAt: parseDate(getMappedValue(row, 'closedAt', 'deals')),
      expectedCloseDate: parseDate(getMappedValue(row, 'expectedCloseDate', 'deals')),
      lostReason: outcome === 'lost' ? getMappedValue(row, 'lostReason', 'deals') : undefined,
      description: `Imported from Pipedrive. Original ID: ${getMappedValue(row, 'pipedriveDealId', 'deals')}`,
    };
  };

  // Load existing leads from Firestore (for linking deals)
  const loadExistingLeads = async (): Promise<ImportedLead[]> => {
    const useTestLeads = localStorage.getItem('crm_use_test_leads') === 'true';
    const collectionName = useTestLeads ? 'testLeads' : 'leads';

    const snapshot = await getDocs(collection(db, collectionName));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      pipedrivePersonId: doc.data().pipedrivePersonId,
      firstName: doc.data().firstName,
      lastName: doc.data().lastName,
      email: doc.data().email,
    }));
  };

  // Import People
  const importPeople = async () => {
    const data = parsedData.people;
    if (!data.length) return { imported: 0, errors: 0, leads: [] as ImportedLead[] };

    const useTestLeads = localStorage.getItem('crm_use_test_leads') === 'true';
    const collectionName = useTestLeads ? 'testLeads' : 'leads';

    let imported = 0;
    let errors = 0;
    const newLeads: ImportedLead[] = [];

    for (let i = 0; i < data.length; i++) {
      try {
        const leadData = personToLead(data[i]);
        const now = new Date();

        // Geocode if enabled
        let coordinates = undefined;
        let isGeocoded = false;

        if (shouldGeocode && leadData.location) {
          setStatus({ type: 'info', message: `Geocoding person ${i + 1}/${data.length}: ${leadData.location.substring(0, 25)}...` });
          try {
            coordinates = await geocodeAddress(leadData.location);
            if (coordinates) isGeocoded = true;
          } catch (e) { /* ignore */ }
        }

        // Clean undefined values
        const cleanedData: Record<string, any> = {};
        for (const [key, value] of Object.entries(leadData)) {
          if (value !== undefined && value !== null && value !== '') {
            cleanedData[key] = value;
          }
        }

        const docRef = await addDoc(collection(db, collectionName), {
          ...cleanedData,
          coordinates: coordinates || { lat: 36.85, lng: -75.4 },
          geocoded: isGeocoded,
          createdAt: leadData.createdAt ? Timestamp.fromDate(leadData.createdAt) : Timestamp.fromDate(now),
          updatedAt: leadData.updatedAt ? Timestamp.fromDate(leadData.updatedAt) : Timestamp.fromDate(now),
        });

        newLeads.push({
          id: docRef.id,
          pipedrivePersonId: leadData.pipedrivePersonId,
          firstName: leadData.firstName || 'Unknown',
          lastName: leadData.lastName || '',
          email: leadData.email || '',
        });

        imported++;
      } catch (err) {
        console.error('Error importing person', i, err);
        errors++;
      }

      setProgress(Math.round(((i + 1) / data.length) * 100));
    }

    return { imported, errors, leads: newLeads };
  };

  // Import Pipedrive Leads - UPDATES existing People records only
  // Only updates: metricsLiteId, location (project address), source, labels
  const importPipedriveLeads = async (existingLeads: ImportedLead[]) => {
    const data = parsedData.leads;
    if (!data.length) return { imported: 0, errors: 0, skipped: 0, linked: 0 };

    const useTestLeads = localStorage.getItem('crm_use_test_leads') === 'true';
    const collectionName = useTestLeads ? 'testLeads' : 'leads';

    let updated = 0;
    let errors = 0;
    let skipped = 0;

    for (let i = 0; i < data.length; i++) {
      try {
        const row = data[i];

        // Get Person ID to match
        const personId = getMappedValue(row, 'personId', 'leads');

        if (!personId) {
          // No Person ID - can't match, skip
          skipped++;
          setProgress(Math.round(((i + 1) / data.length) * 100));
          continue;
        }

        // Find existing lead by Pipedrive Person ID
        const matchingLead = existingLeads.find(l => l.pipedrivePersonId === personId);

        if (!matchingLead) {
          // No matching person found - skip
          skipped++;
          setProgress(Math.round(((i + 1) / data.length) * 100));
          continue;
        }

        setStatus({ type: 'info', message: `Updating ${i + 1}/${data.length}: ${matchingLead.firstName} ${matchingLead.lastName}...` });

        // Extract ONLY the fields we want to update
        const metricsLiteId = getMappedValue(row, 'metricsLiteId', 'leads');
        const projectAddress = getMappedValue(row, 'projectAddress', 'leads');
        const sourceStr = getMappedValue(row, 'source', 'leads');
        const labelsStr = getMappedValue(row, 'labels', 'leads');

        // Build update object with only non-empty values
        const updates: Record<string, any> = {
          updatedAt: Timestamp.fromDate(new Date()),
        };

        // MetricsLite ID
        if (metricsLiteId) {
          updates.metricsLiteId = metricsLiteId;
        }

        // Project Address → location
        if (projectAddress) {
          const formattedLocation = formatLocationForMaps(projectAddress);
          updates.location = formattedLocation;

          // Geocode if enabled
          if (shouldGeocode && formattedLocation) {
            try {
              const coords = await geocodeAddress(formattedLocation);
              if (coords) {
                updates.coordinates = coords;
                updates.geocoded = true;
              }
            } catch (e) { /* ignore */ }
          }
        }

        // Source
        if (sourceStr) {
          updates.source = mapSource(sourceStr);
        }

        // Labels (merge with existing, don't replace)
        if (labelsStr) {
          const newLabels = labelsStr.split(/[,;]/).map(l => l.trim()).filter(l => l);
          if (newLabels.length > 0) {
            // We'll add these labels - the existing ones are preserved by using arrayUnion-like logic
            // For simplicity, we'll just set these labels (can be changed to merge)
            updates.labels = newLabels;
          }
        }

        // Update the existing lead document
        const leadRef = doc(db, collectionName, matchingLead.id);
        await updateDoc(leadRef, updates);

        updated++;
      } catch (err) {
        console.error('Error updating lead', i, err);
        errors++;
      }

      setProgress(Math.round(((i + 1) / data.length) * 100));
    }

    return { imported: updated, errors, skipped, linked: updated };
  };

  // Import Deals
  const importDeals = async (existingLeads: ImportedLead[]) => {
    const data = parsedData.deals;
    if (!data.length) return { imported: 0, errors: 0, linked: 0 };

    let imported = 0;
    let errors = 0;
    let linked = 0;

    for (let i = 0; i < data.length; i++) {
      try {
        setStatus({ type: 'info', message: `Importing deal ${i + 1}/${data.length}...` });

        const dealData = await dealToEnhancedDeal(data[i], existingLeads);
        if (!dealData) {
          errors++;
          continue;
        }

        const now = new Date();

        if (dealData.leadId) linked++;

        const cleanedData: Record<string, any> = {};
        for (const [key, value] of Object.entries(dealData)) {
          if (value !== undefined && value !== null && value !== '') {
            if (key === 'stageHistory') {
              cleanedData[key] = (value as any[]).map(sh => ({
                ...sh,
                transitionedAt: Timestamp.fromDate(sh.transitionedAt),
              }));
            } else if (value instanceof Date) {
              cleanedData[key] = Timestamp.fromDate(value);
            } else {
              cleanedData[key] = value;
            }
          }
        }

        await addDoc(collection(db, 'enhancedDeals'), {
          ...cleanedData,
          currentStageEnteredAt: Timestamp.fromDate(dealData.currentStageEnteredAt || now),
          createdAt: dealData.createdAt ? Timestamp.fromDate(dealData.createdAt) : Timestamp.fromDate(now),
          updatedAt: dealData.updatedAt ? Timestamp.fromDate(dealData.updatedAt) : Timestamp.fromDate(now),
        });

        imported++;
      } catch (err) {
        console.error('Error importing deal', i, err);
        errors++;
      }

      setProgress(Math.round(((i + 1) / data.length) * 100));
    }

    return { imported, errors, linked };
  };

  // Start full import
  const startImport = async () => {
    setImporting(true);
    setProgress(0);

    const results: string[] = [];

    try {
      // 1. Import People first (creates leads)
      if (parsedData.people.length > 0) {
        setStatus({ type: 'info', message: 'Importing People as Leads...' });
        const { imported, errors, leads } = await importPeople();
        setImportedLeads(leads);
        results.push(`People: ${imported} imported${errors > 0 ? `, ${errors} failed` : ''}`);
      }

      // 2. Import Pipedrive Leads (linked to People via Person ID)
      if (parsedData.leads.length > 0) {
        setStatus({ type: 'info', message: 'Importing Pipedrive Leads...' });
        // Load existing leads to link by Person ID
        const existingLeadsForLinking = await loadExistingLeads();
        const { imported, errors, linked } = await importPipedriveLeads(existingLeadsForLinking);
        results.push(`Leads: ${imported} imported${linked > 0 ? ` (${linked} linked to People)` : ''}${errors > 0 ? `, ${errors} failed` : ''}`);
      }

      // 3. Import Deals (linked to leads)
      if (parsedData.deals.length > 0) {
        setStatus({ type: 'info', message: 'Importing Deals...' });
        // Load all existing leads for linking
        const allLeads = await loadExistingLeads();
        const { imported, errors, linked } = await importDeals(allLeads);
        results.push(`Deals: ${imported} imported (${linked} linked to leads)${errors > 0 ? `, ${errors} failed` : ''}`);
      }

      setStatus({
        type: 'success',
        message: `Import complete! ${results.join(' | ')}`
      });

      // Clear data
      setParsedData({ people: [], deals: [], leads: [] });
      setHeaders({ people: [], deals: [], leads: [] });
      setFiles({ people: null, deals: null, leads: null });

    } catch (err) {
      console.error('Import error:', err);
      setStatus({ type: 'error', message: `Import failed: ${err}` });
    }

    setImporting(false);
  };

  // Update manual mapping
  const updateMapping = (crmField: string, csvColumn: string) => {
    setColumnMappings(prev => ({
      ...prev,
      [activeImport]: {
        ...prev[activeImport],
        [crmField]: csvColumn
      }
    }));
  };

  const totalRows = parsedData.people.length + parsedData.deals.length + parsedData.leads.length;

  if (!user) {
    return <div className="import-container">Please log in to import data.</div>;
  }

  return (
    <div className="pipedrive-import-container">
      <div className="import-header">
        <h1><Package size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />Import from Pipedrive</h1>
        <p>Import People, Deals, and Leads from Pipedrive CSV exports</p>
      </div>

      {/* Import Type Tabs */}
      <div className="import-tabs">
        <button
          className={`tab ${activeImport === 'people' ? 'active' : ''} ${parsedData.people.length > 0 ? 'has-data' : ''}`}
          onClick={() => setActiveImport('people')}
        >
          <User size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />People {parsedData.people.length > 0 && `(${parsedData.people.length})`}
        </button>
        <button
          className={`tab ${activeImport === 'deals' ? 'active' : ''} ${parsedData.deals.length > 0 ? 'has-data' : ''}`}
          onClick={() => setActiveImport('deals')}
        >
          <DollarSign size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Deals {parsedData.deals.length > 0 && `(${parsedData.deals.length})`}
        </button>
        <button
          className={`tab ${activeImport === 'leads' ? 'active' : ''} ${parsedData.leads.length > 0 ? 'has-data' : ''}`}
          onClick={() => setActiveImport('leads')}
        >
          <RefreshCw size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Lead Data {parsedData.leads.length > 0 && `(${parsedData.leads.length})`}
        </button>
      </div>

      {/* Upload Section */}
      <div className="import-section">
        <h2>
          {activeImport === 'people' && '1. Upload People CSV (Creates New Leads)'}
          {activeImport === 'deals' && '1. Upload Deals CSV'}
          {activeImport === 'leads' && '1. Upload Lead Data CSV (Updates Existing People)'}
        </h2>

        {activeImport === 'leads' && (
          <div className="info-banner">
            <strong><AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />This updates existing People only</strong>
            <p>Matches by Person ID and updates: MetricsLite ID, Project Address, Source, Labels</p>
            <p>Does NOT create new leads or overwrite names/emails</p>
          </div>
        )}

        <div className="upload-zone">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => handleFileUpload(e, activeImport)}
            id={`file-upload-${activeImport}`}
            className="file-input"
          />
          <label htmlFor={`file-upload-${activeImport}`} className="upload-label">
            {files[activeImport] ? (
              <><Folder size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />{files[activeImport]!.name}</>
            ) : (
              <><Upload size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Click to select {activeImport === 'leads' ? 'Leads' : activeImport} CSV file</>
            )}
          </label>
        </div>

        {activeImport === 'people' && (
          <p className="upload-hint">
            Export from Pipedrive: <strong>Contacts → People → ⋯ → Export</strong>
          </p>
        )}
        {activeImport === 'deals' && (
          <p className="upload-hint">
            Export from Pipedrive: <strong>Deals → ⋯ → Export deals</strong>
          </p>
        )}
        {activeImport === 'leads' && (
          <p className="upload-hint">
            Export from Pipedrive: <strong>Leads → ⋯ → Export</strong>
          </p>
        )}
      </div>

      {/* Column Mapping */}
      {headers[activeImport].length > 0 && (
        <div className="import-section">
          <h2>2. Review Column Mappings</h2>

          {activeImport === 'leads' && (
            <div className="mapping-groups">
              {/* Key linking fields */}
              <div className="mapping-group">
                <h3><LinkIcon size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Linking Fields (Important!)</h3>
                {['personId', 'metricsLiteId'].map(field => (
                  <div key={field} className="mapping-row highlight">
                    <label>{field === 'personId' ? 'Person ID (links to People)' : 'MetricsLite ID'}</label>
                    <select
                      value={columnMappings[activeImport][field] || ''}
                      onChange={(e) => updateMapping(field, e.target.value)}
                    >
                      <option value="">— Not mapped —</option>
                      {headers[activeImport].map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    {columnMappings[activeImport][field] && <span className="mapped-check"><Check size={14} /></span>}
                  </div>
                ))}
              </div>

              {/* Contact info */}
              <div className="mapping-group">
                <h3><User size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Contact Info</h3>
                {['firstName', 'lastName', 'email', 'phone', 'organizationName'].map(field => (
                  <div key={field} className="mapping-row">
                    <label>{field}</label>
                    <select
                      value={columnMappings[activeImport][field] || ''}
                      onChange={(e) => updateMapping(field, e.target.value)}
                    >
                      <option value="">— Not mapped —</option>
                      {headers[activeImport].map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    {columnMappings[activeImport][field] && <span className="mapped-check"><Check size={14} /></span>}
                  </div>
                ))}
              </div>

              {/* Project details */}
              <div className="mapping-group">
                <h3><MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Project Details</h3>
                {['projectAddress', 'value', 'source', 'labels'].map(field => (
                  <div key={field} className="mapping-row">
                    <label>{field === 'projectAddress' ? 'Project Address' : field}</label>
                    <select
                      value={columnMappings[activeImport][field] || ''}
                      onChange={(e) => updateMapping(field, e.target.value)}
                    >
                      <option value="">— Not mapped —</option>
                      {headers[activeImport].map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    {columnMappings[activeImport][field] && <span className="mapped-check"><Check size={14} /></span>}
                  </div>
                ))}
              </div>

              {/* Dates */}
              <div className="mapping-group">
                <h3><Calendar size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Dates</h3>
                {['createdAt', 'updatedAt'].map(field => (
                  <div key={field} className="mapping-row">
                    <label>{field}</label>
                    <select
                      value={columnMappings[activeImport][field] || ''}
                      onChange={(e) => updateMapping(field, e.target.value)}
                    >
                      <option value="">— Not mapped —</option>
                      {headers[activeImport].map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    {columnMappings[activeImport][field] && <span className="mapped-check"><Check size={14} /></span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeImport !== 'leads' && (
            <div className="mappings-grid">
              {Object.keys(getMappingDefs(activeImport)).slice(0, 12).map(field => (
                <div key={field} className="mapping-row">
                  <label>{field}</label>
                  <select
                    value={columnMappings[activeImport][field] || ''}
                    onChange={(e) => updateMapping(field, e.target.value)}
                  >
                    <option value="">— Not mapped —</option>
                    {headers[activeImport].map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  {columnMappings[activeImport][field] && <span className="mapped-check">✓</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview */}
      {parsedData[activeImport].length > 0 && (
        <div className="import-section">
          <h2>3. Preview ({parsedData[activeImport].length} rows)</h2>
          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  {activeImport === 'people' && <><th>Name</th><th>Email</th><th>Phone</th><th>Company</th><th>Person ID</th></>}
                  {activeImport === 'deals' && <><th>Title</th><th>Value</th><th>Person</th><th>Status</th></>}
                  {activeImport === 'leads' && <><th>Person ID</th><th>Project Address</th><th>Source</th><th>Labels</th><th>MetricsLite ID</th></>}
                </tr>
              </thead>
              <tbody>
                {parsedData[activeImport].slice(0, 8).map((row, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    {activeImport === 'people' && (
                      <>
                        <td>{getMappedValue(row, 'firstName', 'people')} {getMappedValue(row, 'lastName', 'people')}</td>
                        <td>{getMappedValue(row, 'email', 'people') || '—'}</td>
                        <td>{getMappedValue(row, 'phone', 'people') || '—'}</td>
                        <td>{getMappedValue(row, 'company', 'people') || '—'}</td>
                        <td className="id-cell">{getMappedValue(row, 'pipedrivePersonId', 'people') || '—'}</td>
                      </>
                    )}
                    {activeImport === 'deals' && (
                      <>
                        <td>{getMappedValue(row, 'title', 'deals') || '—'}</td>
                        <td>${getMappedValue(row, 'value', 'deals') || '0'}</td>
                        <td>{getMappedValue(row, 'personName', 'deals') || '—'}</td>
                        <td>{getMappedValue(row, 'status', 'deals') || '—'}</td>
                      </>
                    )}
                    {activeImport === 'leads' && (
                      <>
                        <td className="id-cell">{getMappedValue(row, 'personId', 'leads') || <span style={{ color: '#ef4444' }}><AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 2 }} />Missing</span>}</td>
                        <td>{getMappedValue(row, 'projectAddress', 'leads')?.substring(0, 30) || '—'}</td>
                        <td>{getMappedValue(row, 'source', 'leads') || '—'}</td>
                        <td>{getMappedValue(row, 'labels', 'leads') || '—'}</td>
                        <td className="id-cell">{getMappedValue(row, 'metricsLiteId', 'leads') || '—'}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pipeline Selection for Deals */}
      {parsedData.deals.length > 0 && (
        <div className="import-section">
          <h2><LinkIcon size={18} style={{ verticalAlign: 'middle', marginRight: 4 }} />Select Pipeline for Deals</h2>
          <p className="section-desc">Imported deals will be added to this pipeline:</p>
          <div className="pipeline-selector">
            {pipelines.map(pipeline => (
              <button
                key={pipeline.id}
                type="button"
                className={`pipeline-option ${selectedPipelineId === pipeline.id ? 'selected' : ''}`}
                onClick={() => pipeline.id && setSelectedPipelineId(pipeline.id)}
              >
                <div className="pipeline-color" style={{ backgroundColor: pipeline.color }} />
                <span>{pipeline.name}</span>
                {pipeline.isDefault && <span className="default-badge">Default</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Import Options */}
      {totalRows > 0 && (
        <div className="import-section">
          <h2>4. Import Options</h2>
          <label className="checkbox-option">
            <input
              type="checkbox"
              checked={shouldGeocode}
              onChange={(e) => setShouldGeocode(e.target.checked)}
            />
            <span>Geocode locations (slower but shows on map)</span>
          </label>
        </div>
      )}

      {/* Summary */}
      {totalRows > 0 && (
        <div className="import-summary">
          <h3>Ready to Import:</h3>
          <div className="summary-items">
            {parsedData.people.length > 0 && (
              <div className="summary-item"><User size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{parsedData.people.length} People → <strong>Create</strong> Leads</div>
            )}
            {parsedData.leads.length > 0 && (
              <div className="summary-item"><RefreshCw size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{parsedData.leads.length} Lead Data → <strong>Update</strong> existing People (ML ID, Address, Source, Labels)</div>
            )}
            {parsedData.deals.length > 0 && (
              <div className="summary-item"><DollarSign size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{parsedData.deals.length} Deals → Pipeline</div>
            )}
          </div>
        </div>
      )}

      {/* Status & Progress */}
      {status && (
        <div className={`status-message status-${status.type}`}>
          {status.message}
        </div>
      )}

      {importing && (
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
          <span className="progress-text">{progress}%</span>
        </div>
      )}

      {/* Import Button */}
      {totalRows > 0 && !importing && (
        <div className="import-actions">
          <button className="import-btn" onClick={startImport}>
            <Rocket size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Import All ({totalRows} records)
          </button>
        </div>
      )}

      {/* Help */}
      <div className="import-section help-section">
        <h2><Lightbulb size={18} style={{ verticalAlign: 'middle', marginRight: 4 }} />Import Order</h2>
        <ol>
          <li><strong>People first</strong> - Creates leads with Pipedrive Person IDs</li>
          <li><strong>Lead Data second</strong> - Updates existing People with: MetricsLite ID, Project Address, Source, Labels</li>
          <li><strong>Deals last</strong> - Links to leads by Person ID or name matching</li>
        </ol>
        <p>You can upload files in any order, but they'll be processed in this sequence to ensure proper linking.</p>

        <h3 style={{ marginTop: '20px' }}>Lead Data Fields</h3>
        <table className="help-table">
          <tbody>
            <tr><td><strong>Person ID</strong></td><td>Used to match to existing People (required)</td></tr>
            <tr><td><strong>MetricsLite ID</strong></td><td>→ metricsLiteId (for web tracking)</td></tr>
            <tr><td><strong>Project Address</strong></td><td>→ location (geocoded for map)</td></tr>
            <tr><td><strong>Source</strong></td><td>→ source (facebook-form, web-form, etc.)</td></tr>
            <tr><td><strong>Labels</strong></td><td>→ labels (comma-separated tags)</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportPipedrive;
